import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ShieldCheck, PackageX, ClipboardCheck, PauseCircle, Settings } from "lucide-react";

/**
 * QC Dashboard — Iter-88.
 * Shows GRN Inward / Outward / SRN QC summary + config panel for
 * (100% vs Sample) inspection mode + hold-stock-on-fail toggle.
 */
export default function QCDashboard() {
  const [cfg, setCfg] = useState(null);
  const [dash, setDash] = useState(null);
  const [records, setRecords] = useState([]);

  const load = async () => {
    const [c, d, r] = await Promise.all([
      api.get("/ops/qc/config"),
      api.get("/ops/qc/dashboard"),
      api.get("/ops/qc/records"),
    ]);
    setCfg(c.data); setDash(d.data); setRecords(r.data.records || []);
  };
  useEffect(() => { load(); }, []);

  const saveCfg = async () => {
    try { await api.put("/ops/qc/config", cfg); toast.success("QC policy saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  if (!cfg || !dash) return <div className="p-6 text-muted-foreground">Loading QC…</div>;

  const kpi = (label, val, Icon, tone) => (
    <Card className={`p-4 rounded-sm border ${tone}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest">{label}</div>
        <Icon className="w-4 h-4 opacity-70" />
      </div>
      <div className="text-3xl font-bold mt-2 tabular-nums">{val}</div>
    </Card>
  );

  const total = dash.summary.reduce((a, b) => a + b.count, 0);
  const pass  = dash.summary.filter(x => x._id.outcome === "pass").reduce((a, b) => a + b.count, 0);
  const fail  = dash.summary.filter(x => x._id.outcome === "fail").reduce((a, b) => a + b.count, 0);
  const rework= dash.summary.filter(x => x._id.outcome === "rework").reduce((a, b) => a + b.count, 0);

  return (
    <div className="p-6 space-y-6" data-testid="qc-dashboard">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" /> Quality Control
        </div>
        <h1 className="font-heading text-3xl font-bold mt-1">QC Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {kpi("Inspections", total, ClipboardCheck, "bg-slate-500/5 border-slate-500/20 text-slate-700")}
        {kpi("Passed",   pass,   ShieldCheck,  "bg-emerald-500/5 border-emerald-500/25 text-emerald-700")}
        {kpi("Failed",   fail,   PackageX,     "bg-red-500/5     border-red-500/25     text-red-700")}
        {kpi("Rework",   rework, PauseCircle,  "bg-amber-500/5   border-amber-500/25   text-amber-700")}
        {kpi("GRN Holds", dash.grn_holds, PauseCircle, "bg-orange-500/5 border-orange-500/25 text-orange-700")}
      </div>

      <Card className="p-5 rounded-sm border" data-testid="qc-config-panel">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="w-4 h-4 text-accent" />
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Policy</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>GRN Inward mode</Label>
            <Select value={cfg.grn_inward_mode} onValueChange={v => setCfg({...cfg, grn_inward_mode: v})}>
              <SelectTrigger className="rounded-sm mt-1 h-10" data-testid="qc-grn-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="100_pct">100% inspection</SelectItem>
                <SelectItem value="sample">AQL sample</SelectItem>
                <SelectItem value="skip">Skip (declare only)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Outward pre-dispatch</Label>
            <Select value={cfg.outward_mode} onValueChange={v => setCfg({...cfg, outward_mode: v})}>
              <SelectTrigger className="rounded-sm mt-1 h-10" data-testid="qc-out-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="100_pct">100% pre-dispatch</SelectItem>
                <SelectItem value="sample">Per-batch sample</SelectItem>
                <SelectItem value="skip">Skip</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>SRN mode</Label>
            <Select value={cfg.srn_mode} onValueChange={v => setCfg({...cfg, srn_mode: v})}>
              <SelectTrigger className="rounded-sm mt-1 h-10" data-testid="qc-srn-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="accept">Accept-only (buyer acknowledgement)</SelectItem>
                <SelectItem value="detailed_qc">Detailed QC record</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>AQL sample size %</Label>
            <Input type="number" value={cfg.sample_size_pct}
                     onChange={e => setCfg({...cfg, sample_size_pct: Number(e.target.value)})}
                     className="rounded-sm mt-1 h-10 tabular-nums text-right" data-testid="qc-sample-pct" />
          </div>
          <label className="flex items-center gap-3 mt-6">
            <Switch checked={cfg.hold_stock_on_fail}
                      onCheckedChange={v => setCfg({...cfg, hold_stock_on_fail: v})}
                      data-testid="qc-hold-toggle" />
            <span className="text-sm">Hold stock on Fail (block onward use)</span>
          </label>
          <div className="md:col-span-3 flex justify-end">
            <Button onClick={saveCfg} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="qc-save-cfg">
              Save policy
            </Button>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm border overflow-hidden">
        <div className="p-3 border-b text-[10px] font-mono uppercase tracking-widest text-muted-foreground">// Recent QC records</div>
        <div className="overflow-x-auto max-h-96">
          <table className="w-full text-sm">
            <thead className="text-[10px] font-mono uppercase text-muted-foreground bg-muted/40">
              <tr>
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-left">Kind</th>
                <th className="p-2 text-left">Source</th>
                <th className="p-2 text-right">Passed</th>
                <th className="p-2 text-right">Failed</th>
                <th className="p-2 text-right">Rework</th>
                <th className="p-2 text-left">Outcome</th>
                <th className="p-2 text-left">Inspector</th>
              </tr>
            </thead>
            <tbody>
              {records.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground text-sm">No QC records yet. Records will appear as GRNs and Outward-dispatches are inspected.</td></tr>}
              {records.map((r, i) => (
                <tr key={i} className="border-t hover:bg-muted/30 cursor-pointer" data-testid={`qc-record-${r.id}`}>
                  <td className="p-2 text-[11px] font-mono">{r.date || (r.created_at || "").slice(0, 10)}</td>
                  <td className="p-2 text-[10px] font-mono uppercase">{(r.kind || "").replace("_", " ")}</td>
                  <td className="p-2 font-mono text-[11px]">{r.reference_number || r.source_ref || (r.reference_id || r.source_id || "").slice(0, 8) || "—"}</td>
                  <td className="p-2 text-right font-mono">{r.qty_passed ?? r.passed_qty ?? 0}</td>
                  <td className="p-2 text-right font-mono text-red-600">{r.qty_failed ?? r.failed_qty ?? 0}</td>
                  <td className="p-2 text-right font-mono text-amber-600">{r.qty_rework ?? r.rework_qty ?? 0}</td>
                  <td className="p-2 text-[11px] font-mono uppercase font-semibold">{r.outcome}</td>
                  <td className="p-2 text-[11px]">{r.inspector_name || r.remarks || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
